import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Avatar,
  Typography,
  Space,
  Rate,
  Select,
  Input,
  Modal,
  Form,
  Switch,
  Radio,
  message,
  Popconfirm,
  Empty,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  StarFilled,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  getReviews,
  createReview,
  updateReview,
  deleteReview,
  toggleReviewApproval,
} from "../../api/reviewsApi";
import { getProducts } from "../../api/productsApi";
import { getCustomers } from "../../api/customersApi";

const { Text, Title } = Typography;
const { TextArea } = Input;

// ── Add/Edit Review Modal ─────────────────────────────────────────────────

function ReviewFormModal({ open, onClose, onSaved, editingReview }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [reviewerType, setReviewerType] = useState("manual"); // manual | customer
  const [productOptions, setProductOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);

  // ← جديد: نص البحث الخام (بيتحدث فورًا مع كل حرف)
  const [productSearchText, setProductSearchText] = useState("");
  const [customerSearchText, setCustomerSearchText] = useState("");

  const isEdit = !!editingReview;

  useEffect(() => {
    if (open) {
      if (editingReview) {
        setReviewerType(editingReview.customer_id ? "customer" : "manual");
        form.setFieldsValue({
          rating: editingReview.rating,
          title: editingReview.title,
          body: editingReview.body,
          reviewer_name: editingReview.reviewer_name,
          is_approved: editingReview.is_approved,
        });
      } else {
        form.resetFields();
        setReviewerType("manual");
        form.setFieldsValue({ is_approved: true, rating: 5 });
      }
    }
  }, [open, editingReview, form]);

  const searchProducts = useCallback(async (q) => {
    setProductLoading(true);
    try {
      const { data } = await getProducts({ search: q, page_size: 15 });
      const results = data?.results ?? data?.data?.results ?? data?.data ?? [];
      setProductOptions(results);
    } finally {
      setProductLoading(false);
    }
  }, []);

  const searchCustomers = useCallback(async (q) => {
    setCustomerLoading(true);
    try {
      const { data } = await getCustomers({ search: q, page_size: 15 });
      const results = data?.results ?? data?.data?.results ?? data?.data ?? [];
      setCustomerOptions(results);
    } finally {
      setCustomerLoading(false);
    }
  }, []);

  // ← جديد: تحميل أولي لما المودال يفتح (وضع إضافة فقط)
  useEffect(() => {
    if (open && !isEdit) {
      searchProducts("");
      searchCustomers("");
      setProductSearchText("");
      setCustomerSearchText("");
    }
  }, [open, isEdit, searchProducts, searchCustomers]);

  // ← جديد: debounce على بحث المنتجات (400ms زي SearchModal)
  useEffect(() => {
    if (!open || isEdit) return;
    const t = setTimeout(() => {
      searchProducts(productSearchText);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSearchText, open, isEdit]);

  // ← جديد: debounce على بحث العملاء (400ms زي SearchModal)
  useEffect(() => {
    if (!open || isEdit || reviewerType !== "customer") return;
    const t = setTimeout(() => {
      searchCustomers(customerSearchText);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearchText, open, isEdit, reviewerType]);
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (isEdit) {
        // تعديل: مسموح تعدّل التقييم/العنوان/النص/الاسم اليدوي (لو مش عميل حقيقي)/حالة التفعيل
        const payload = {
          rating: values.rating,
          title: values.title,
          body: values.body,
          is_approved: values.is_approved,
        };
        if (!editingReview.customer_id) {
          payload.reviewer_name = values.reviewer_name;
        }
        await updateReview(editingReview.id, payload);
        message.success("تم تحديث التقييم بنجاح.");
      } else {
        const payload = {
          product_id: values.product_id,
          rating: values.rating,
          title: values.title,
          body: values.body,
          is_approved: values.is_approved,
        };
        if (reviewerType === "customer") {
          payload.user_id = values.user_id;
        } else {
          payload.reviewer_name = values.reviewer_name;
        }
        await createReview(payload);
        message.success("تم إضافة التقييم بنجاح.");
      }

      onSaved();
      onClose();
    } catch (err) {
      if (err?.errorFields) return; // validation error محلي
      const apiErrors = err?.response?.data?.errors;
      const msg =
        (apiErrors && JSON.stringify(apiErrors)) ||
        err?.response?.data?.message ||
        "حدث خطأ، حاول مرة أخرى.";
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={saving}
      title={isEdit ? "تعديل التقييم" : "إضافة تقييم جديد"}
      okText={isEdit ? "حفظ التعديلات" : "إضافة"}
      cancelText="إلغاء"
      width={560}
      style={{ direction: "rtl" }}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        {!isEdit && (
          <Form.Item
            label="المنتج"
            name="product_id"
            rules={[{ required: true, message: "اختر المنتج" }]}
          >
            <Select
                showSearch
                placeholder="ابحث عن منتج..."
                filterOption={false}
                onSearch={setProductSearchText}
                loading={productLoading}
                options={productOptions.map((p) => ({
                    value: p.id,
                    label: p.name,
                }))}
                />
          </Form.Item>
        )}

        {!isEdit && (
          <Form.Item label="نوع المُقيِّم">
            <Radio.Group
              value={reviewerType}
              onChange={(e) => setReviewerType(e.target.value)}
            >
              <Radio.Button value="manual">اسم يدوي (بدون عميل)</Radio.Button>
              <Radio.Button value="customer">عميل حقيقي مسجل</Radio.Button>
            </Radio.Group>
          </Form.Item>
        )}

        {(isEdit && !editingReview.customer_id) ||
        (!isEdit && reviewerType === "manual") ? (
          <Form.Item
            label="اسم المُقيِّم"
            name="reviewer_name"
            rules={[{ required: true, message: "اكتب اسم المُقيِّم" }]}
          >
            <Input placeholder="مثال: سارة محمود" prefix={<UserOutlined />} />
          </Form.Item>
        ) : null}

        {!isEdit && reviewerType === "customer" && (
          <Form.Item
            label="العميل"
            name="user_id"
            rules={[{ required: true, message: "اختر العميل" }]}
          >
            <Select
                showSearch
                placeholder="ابحث عن عميل..."
                filterOption={false}
                onSearch={setCustomerSearchText}
                loading={customerLoading}
                options={customerOptions.map((c) => ({
                    value: c.id,
                    label: `${c.full_name} — ${c.email}`,
                }))}
                />
          </Form.Item>
        )}

        {isEdit && editingReview.customer_id && (
          <Form.Item label="المُقيِّم">
            <Text type="secondary">
              هذا التقييم مرتبط بعميل حقيقي، ولا يمكن تغيير اسمه.
            </Text>
          </Form.Item>
        )}

        <Form.Item
          label="التقييم"
          name="rating"
          rules={[{ required: true, message: "اختر عدد النجوم" }]}
        >
          <Rate />
        </Form.Item>

        <Form.Item label="عنوان التقييم (اختياري)" name="title">
          <Input placeholder="مثال: منتج ممتاز" />
        </Form.Item>

        <Form.Item
          label="نص التقييم"
          name="body"
          rules={[{ required: true, message: "اكتب نص التقييم" }]}
        >
          <TextArea rows={4} placeholder="اكتب تفاصيل التقييم هنا..." />
        </Form.Item>

        <Form.Item
          label="ظاهر للعملاء؟"
          name="is_approved"
          valuePropName="checked"
        >
          <Switch checkedChildren="مفعّل" unCheckedChildren="مخفي" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [search, setSearch] = useState("");
  const [approvalFilter, setApprovalFilter] = useState(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);

  const fetchReviews = useCallback(
    async (page = 1, pageSize = 10) => {
      setLoading(true);
      try {
        const { data } = await getReviews({
          page,
          page_size: pageSize,
          search: search || undefined,
          is_approved: approvalFilter,
        });
        const payload = data?.data ?? data;
        const results = payload?.results ?? payload ?? [];
        const count = payload?.count ?? results.length;
        setReviews(results);
        setPagination({ current: page, pageSize, total: count });
      } catch (e) {
        message.error("حدث خطأ أثناء تحميل التقييمات.");
      } finally {
        setLoading(false);
      }
    },
    [search, approvalFilter]
  );

  useEffect(() => {
    fetchReviews(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, approvalFilter]);

  const handleDelete = async (id) => {
    try {
      await deleteReview(id);
      message.success("تم حذف التقييم.");
      fetchReviews(pagination.current, pagination.pageSize);
    } catch {
      message.error("فشل حذف التقييم.");
    }
  };

  const handleToggleApproval = async (id) => {
    try {
      const { data } = await toggleReviewApproval(id);
      message.success(data?.message || "تم التحديث.");
      fetchReviews(pagination.current, pagination.pageSize);
    } catch {
      message.error("فشل تحديث حالة التقييم.");
    }
  };

  const openAddModal = () => {
    setEditingReview(null);
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingReview(record);
    setModalOpen(true);
  };

  const columns = [
    {
      title: "المنتج",
      dataIndex: "product_name",
      render: (v, r) => (
        <div>
          <Text style={{ fontWeight: 600, display: "block", fontSize: 13 }}>
            {v}
          </Text>
          <Text style={{ color: "#94A3B8", fontSize: 11 }}>
            #{r.product}
          </Text>
        </div>
      ),
    },
    {
      title: "المُقيِّم",
      dataIndex: "reviewer_name",
      render: (v, r) => (
        <Space>
          <Avatar size={28} style={{ background: "#6366F1", fontSize: 12 }}>
            {v?.[0] || "؟"}
          </Avatar>
          <div>
            <Text style={{ fontSize: 13, display: "block" }}>{v}</Text>
            {r.is_admin_created && (
              <Tag color="purple" style={{ fontSize: 10, lineHeight: "16px" }}>
                {r.customer_id ? "أضافه الأدمن لعميل" : "أضافه الأدمن"}
              </Tag>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: "التقييم",
      dataIndex: "rating",
      width: 140,
      render: (v) => <Rate disabled defaultValue={v} style={{ fontSize: 14 }} />,
      sorter: (a, b) => a.rating - b.rating,
    },
    {
      title: "النص",
      dataIndex: "body",
      ellipsis: true,
      width: 260,
      render: (v, r) => (
        <div>
          {r.title && (
            <Text style={{ fontWeight: 600, fontSize: 13, display: "block" }}>
              {r.title}
            </Text>
          )}
          <Text style={{ color: "#64748B", fontSize: 12 }} ellipsis>
            {v}
          </Text>
        </div>
      ),
    },
    {
      title: "الحالة",
      dataIndex: "is_approved",
      width: 110,
      render: (v) =>
        v ? (
          <Tag color="green" style={{ borderRadius: 6 }}>
            ظاهر
          </Tag>
        ) : (
          <Tag color="default" style={{ borderRadius: 6 }}>
            مخفي
          </Tag>
        ),
    },
    {
      title: "تاريخ",
      dataIndex: "created_at",
      width: 110,
      render: (v) => (
        <Text style={{ color: "#94A3B8", fontSize: 12 }}>
          {v ? new Date(v).toLocaleDateString("ar-EG") : "-"}
        </Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title={record.is_approved ? "إخفاء" : "إظهار"}>
            <Button
              type="text"
              size="small"
              icon={
                record.is_approved ? <EyeInvisibleOutlined /> : <EyeOutlined />
              }
              onClick={() => handleToggleApproval(record.id)}
            />
          </Tooltip>
          <Tooltip title="تعديل">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="حذف التقييم"
            description="هل أنت متأكد من حذف هذا التقييم؟"
            onConfirm={() => handleDelete(record.id)}
            okText="حذف"
            cancelText="إلغاء"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="حذف">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ direction: "rtl" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0, color: "#0F172A" }}>
            <StarFilled style={{ color: "#F59E0B", marginLeft: 8 }} />
            تقييمات المنتجات
          </Title>
          <Text style={{ color: "#94A3B8", fontSize: 13 }}>
            إدارة تقييمات العملاء، وإضافة تقييمات جديدة يدوياً
          </Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
          إضافة تقييم
        </Button>
      </div>

      <Card style={{ borderRadius: 16, border: "1px solid #E2E8F0" }}>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="بحث بالاسم أو نص التقييم..."
            prefix={<SearchOutlined style={{ color: "#94A3B8" }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 260 }}
            allowClear
          />
          <Select
            placeholder="حالة التقييم"
            allowClear
            style={{ width: 160 }}
            value={approvalFilter}
            onChange={setApprovalFilter}
            options={[
              { value: "true", label: "ظاهر فقط" },
              { value: "false", label: "مخفي فقط" },
            ]}
          />
        </Space>

        <Table
          loading={loading}
          dataSource={reviews}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            onChange: (page, pageSize) => fetchReviews(page, pageSize),
          }}
          locale={{
            emptyText: <Empty description="لا توجد تقييمات" />,
          }}
        />
      </Card>

      <ReviewFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={() => fetchReviews(pagination.current, pagination.pageSize)}
        editingReview={editingReview}
      />
    </div>
  );
}